import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Stack,
  Group,
  Table,
  Badge,
  Text,
  TextInput,
  SegmentedControl,
  Button,
  Loader,
  Alert,
  Drawer,
  Modal,
  Code,
  Divider,
  Tooltip,
  Card,
  Center,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { formatApiError } from '@/api/errors'

import { Capacidades } from './coverage/Capacidades'
import {
  getCoverage,
  refreshCoverage,
  provisionRole,
  getProvisionJob,
  type CoverageRow,
  type CapacidadesDoTenant,
  getCapacidadesPorTenant,
  type CoverageStatus,
  type ScanRun,
  type ProvisionJob,
  type CoStatus,
} from '@/api/awsRoleCoverage'
import { FinopsDetectionHealthCard } from './FinopsDetectionHealthCard'

// ============================================================
// AWS Role Coverage — tela matriz (Task 14 / TASK-2720)
//
// A API devolve uma linha por (conta × role). Aqui pivotamos por account_id:
// cada conta vira UMA linha da matriz e as 4 roles viram colunas em ordem fixa.
//
// NOTA DE ESCOPO: o payload NÃO traz a OU (unidade organizacional) da conta,
// apenas account_id + tenant_id. Por isso NÃO agrupamos por OU — o agrupamento
// por OU fica para evolução futura (exigiria o backend devolver o caminho da OU).
// Mostramos tenant_id ao lado do account_id como contexto disponível.
//
// Os status possíveis (fonte: backend aws_role_coverage.py) são:
//   ok | missing | trust_broken | denied | error | not_applicable
// CoverageStatus (client) espelha exatamente esse conjunto.
// ============================================================

/** Ordem fixa das colunas (roles) na matriz. */
const ROLE_COLUMNS = [
  'ConsoleAIReadRole',
  'OrganizationAccountAccessRole',
  'ConsoleAIFinopsReadRole',
  'ConsoleAIFinopsCleanupRole',
] as const

/** Rótulo curto para o cabeçalho da coluna. */
const ROLE_LABELS: Record<string, string> = {
  ConsoleAIReadRole: 'Read',
  OrganizationAccountAccessRole: 'OrgAccess',
  ConsoleAIFinopsReadRole: 'Finops Read',
  ConsoleAIFinopsCleanupRole: 'Finops Cleanup',
}

/**
 * Roles que podem ser provisionadas via StackSet pelo ConsoleAI.
 * OrganizationAccountAccessRole NÃO é provisionável (gerida pela AWS Org).
 */
const PROVISIONABLE_ROLES = new Set<string>([
  'ConsoleAIReadRole',
  'ConsoleAIFinopsReadRole',
  'ConsoleAIFinopsCleanupRole',
])

/**
 * Roles FinOps — são as únicas para as quais o indicador de actions é exibido.
 */
const FINOPS_ROLES = new Set<string>([
  'ConsoleAIFinopsReadRole',
  'ConsoleAIFinopsCleanupRole',
])

/** Mapa co_status → cor Mantine. */
const CO_STATUS_COLOR: Record<CoStatus, string> = {
  active: 'green',
  inactive: 'gray',
  pending: 'yellow',
  failed: 'red',
  unknown: 'gray',
}

/** Mapa co_status → rótulo PT-BR. */
const CO_STATUS_LABEL: Record<CoStatus, string> = {
  active: 'Ativa',
  inactive: 'Inativa',
  pending: 'Pendente',
  failed: 'Falha',
  unknown: 'Desconhecido',
}

/** Mapa status → cor do Badge (Mantine). */
const STATUS_COLOR: Record<string, string> = {
  ok: 'green',
  missing: 'red',
  trust_broken: 'yellow',
  denied: 'gray',
  error: 'orange',
  not_applicable: 'dimmed',
}

/** Timeout de segurança para o polling de varredura: 5 minutos. */
const SCAN_POLLING_TIMEOUT_MS = 5 * 60 * 1000
/** Intervalo de polling durante a varredura. */
const SCAN_POLLING_INTERVAL_MS = 5000
/**
 * Timeout de segurança para o polling de job de provisionamento: 5 minutos.
 * O job backend aguarda até 5 min pelo StackSet (poll_timeout_seconds=300),
 * portanto o frontend precisa de um cap compatível.
 */
const PROVISION_JOB_POLL_TIMEOUT_MS = 5 * 60 * 1000
/** Intervalo de polling do job de provisionamento. */
const PROVISION_JOB_POLL_INTERVAL_MS = 3000

function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? 'gray'
}

/**
 * Retorna o status de uma row, ou 'not_applicable' quando a row está ausente
 * (conta payer sem detecção de role, ou role não encontrada no resultado).
 */
function rowStatus(row: CoverageRow | undefined): CoverageStatus {
  return row?.status ?? 'not_applicable'
}

/** Status que contam como "gap" (precisa de atenção). */
function isGap(status: string): boolean {
  return status !== 'ok' && status !== 'not_applicable'
}

/** Uma conta pivotada: account_id + tenant_id + nome/env do tenant + mapa role→row. */
interface AccountMatrixRow {
  account_id: string
  tenant_id: number | null
  tenant_name: string | null
  tenant_environment: string | null
  byRole: Record<string, CoverageRow | undefined>
  /** Status de ativação no ConsoleAI (derivado do primeiro co_status disponível entre as rows). */
  co_status: CoStatus | undefined
}

// ============================================================
// Componentes auxiliares inline
// ============================================================

/**
 * Indicador compacto de prontidão de actions FinOps.
 * Exibido apenas em células status=ok de roles FinOps.
 */
function ActionsIndicator({ row }: { row: CoverageRow }) {
  const { actions_status, missing_actions } = row
  if (!actions_status || actions_status === 'not_checked') return null

  if (actions_status === 'complete') {
    return (
      <Tooltip label="Actions FinOps: completas" withArrow>
        <ThemeIcon
          size="xs"
          color="teal"
          variant="light"
          radius="xl"
          data-testid={`actions-indicator-complete-${row.account_id}-${row.role_name}`}
          style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}
        >
          <span style={{ fontSize: 8 }}>✓</span>
        </ThemeIcon>
      </Tooltip>
    )
  }

  if (actions_status === 'actions_missing') {
    const missingList =
      missing_actions && missing_actions.length > 0
        ? missing_actions.join(', ')
        : 'ações não especificadas'
    return (
      <Tooltip
        label={`Actions faltantes: ${missingList}`}
        withArrow
        multiline
        w={280}
      >
        <ThemeIcon
          size="xs"
          color="red"
          variant="light"
          radius="xl"
          data-testid={`actions-indicator-missing-${row.account_id}-${row.role_name}`}
          style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}
        >
          <span style={{ fontSize: 8 }}>!</span>
        </ThemeIcon>
      </Tooltip>
    )
  }

  if (actions_status === 'validation_unavailable') {
    return (
      <Tooltip label="Validação de actions indisponível" withArrow>
        <ThemeIcon
          size="xs"
          color="gray"
          variant="light"
          radius="xl"
          data-testid={`actions-indicator-unavailable-${row.account_id}-${row.role_name}`}
          style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}
        >
          <span style={{ fontSize: 8 }}>?</span>
        </ThemeIcon>
      </Tooltip>
    )
  }

  return null
}

/**
 * Badge compacto de co_status por conta.
 */
function CoStatusChip({ status }: { status: CoStatus }) {
  return (
    <Badge
      size="xs"
      color={CO_STATUS_COLOR[status]}
      variant="light"
      data-testid={`co-status-${status}`}
    >
      {CO_STATUS_LABEL[status]}
    </Badge>
  )
}

/** Pivota as rows planas (conta×role) em linhas por conta. */
function pivotByAccount(rows: CoverageRow[]): AccountMatrixRow[] {
  const map = new Map<string, AccountMatrixRow>()
  for (const r of rows) {
    let acc = map.get(r.account_id)
    if (!acc) {
      acc = {
        account_id: r.account_id,
        tenant_id: r.tenant_id,
        tenant_name: r.tenant_name ?? null,
        tenant_environment: r.tenant_environment ?? null,
        byRole: {},
        co_status: r.co_status,
      }
      map.set(r.account_id, acc)
    }
    acc.byRole[r.role_name] = r
    // Mantém o primeiro co_status não-undefined encontrado.
    if (acc.co_status === undefined && r.co_status !== undefined) {
      acc.co_status = r.co_status
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.account_id.localeCompare(b.account_id),
  )
}

/** Conta tem ≥1 role em gap? */
function accountHasGap(acc: AccountMatrixRow): boolean {
  return ROLE_COLUMNS.some((role) => {
    const cell = acc.byRole[role]
    return cell ? isGap(cell.status) : false
  })
}

/** Conta tem ≥1 role denied (bloqueada — exige ação manual na AWS)? */
function accountIsBlocked(acc: AccountMatrixRow): boolean {
  return ROLE_COLUMNS.some((role) => rowStatus(acc.byRole[role]) === 'denied')
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR')
}

const TERMINAL_JOB_STATUS = new Set<ProvisionJob['status']>([
  'success',
  'failed',
  'noop',
])

interface SelectedCell {
  account_id: string
  role_name: string
  row: CoverageRow | undefined
}

export function AwsRoleCoverage() {
  const [rows, setRows] = useState<CoverageRow[]>([])
  // Capacidades por tenant, do OUTRO sistema (validador diario de permissoes). Indexadas por
  // account_id para a tabela nao fazer N+1. Falha aqui NAO derruba a tela: a cobertura por papel
  // continua valendo, e a coluna mostra "—" (sem dado), que e honesto.
  const [capacidades, setCapacidades] = useState<Record<string, CapacidadesDoTenant>>({})
  const [scanRun, setScanRun] = useState<ScanRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  /** true enquanto a varredura de background está em andamento */
  const [scanning, setScanning] = useState(false)

  /** started_at da varredura que estava rodando ANTES de disparar um novo refresh */
  const baselineStartedAtRef = useRef<string | null>(null)

  /** Ref para o intervalo de polling da varredura */
  const scanPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** Ref para o timeout de segurança de varredura */
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [filter, setFilter] = useState<'gap' | 'all'>('gap')
  const [search, setSearch] = useState('')

  const [selected, setSelected] = useState<SelectedCell | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Provisionamento em andamento
  const [provJob, setProvJob] = useState<ProvisionJob | null>(null)
  const [provisioning, setProvisioning] = useState(false)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Timestamp (Date.now()) em que o polling de provision foi iniciado. */
  const pollStartedAtRef = useRef<number | null>(null)

  /** Para o polling de varredura e limpa refs. */
  const stopScanPolling = useCallback(() => {
    if (scanPollIntervalRef.current) {
      clearInterval(scanPollIntervalRef.current)
      scanPollIntervalRef.current = null
    }
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current)
      scanTimeoutRef.current = null
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const data = await getCoverage()
      setRows(data.rows ?? [])
      getCapacidadesPorTenant()
        .then((itens) => {
          const porConta: Record<string, CapacidadesDoTenant> = {}
          for (const i of itens) porConta[i.account_id] = i
          setCapacidades(porConta)
        })
        // Silencio proposital: a coluna degrada para "—" e o resto da tela continua util.
        .catch(() => setCapacidades({}))
      setScanRun(data.scan_run ?? null)
      setLoadError(null)
      return data
    } catch (err) {
      setLoadError(formatApiError(err, 'Falha ao carregar cobertura'))
      throw err
    }
  }, [])

  /**
   * Inicia o polling periódico de varredura.
   * Considera a varredura concluída quando o scan_run retornado tiver:
   *   - finished_at != null E
   *   - started_at diferente do baseline (ou id diferente) — ou seja, é um run mais novo.
   */
  const startScanPolling = useCallback(
    (baselineStartedAt: string | null) => {
      stopScanPolling()

      // Timeout de segurança: após 5 min, sai do scanning mesmo sem concluir.
      scanTimeoutRef.current = setTimeout(() => {
        stopScanPolling()
        setScanning(false)
        setRefreshing(false)
        notifications.show({
          color: 'yellow',
          title: 'Varredura demorada',
          message:
            'A varredura está demorando mais que o esperado — recarregue em instantes.',
        })
      }, SCAN_POLLING_TIMEOUT_MS)

      scanPollIntervalRef.current = setInterval(async () => {
        try {
          const data = await getCoverage()
          const sr = data.scan_run ?? null

          // Verifica se a varredura foi concluída: finished_at setado E run diferente do baseline.
          const isNewer =
            sr != null &&
            sr.finished_at != null &&
            (baselineStartedAt === null || sr.started_at !== baselineStartedAt)

          if (isNewer && sr != null) {
            // Varredura concluída — atualiza UI e para o polling.
            setRows(data.rows ?? [])
            setScanRun(sr)
            stopScanPolling()
            setScanning(false)
            setRefreshing(false)

            const ok = sr.accounts_ok ?? 0
            const gap = sr.accounts_with_gap ?? 0
            const total = sr.accounts_total ?? ok + gap
            const blocked = total - ok - gap >= 0 ? total - ok - gap : 0

            notifications.show({
              color: 'green',
              title: 'Varredura concluída',
              message: `${ok} conta${ok !== 1 ? 's' : ''} OK · ${gap} com gap · ${blocked} bloqueada${blocked !== 1 ? 's' : ''}`,
              autoClose: 6000,
            })
          }
        } catch {
          // Ignora erros transitórios durante o polling — tenta de novo no próximo tick.
        }
      }, SCAN_POLLING_INTERVAL_MS)
    },
    [stopScanPolling],
  )

  // Carga inicial
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const data = await load()
        // Se já há uma varredura em andamento ao montar a tela, entra em scanning + polling.
        if (alive && data.scan_run && data.scan_run.finished_at == null) {
          setScanning(true)
          startScanPolling(data.scan_run.started_at)
        }
      } catch {
        // erro já tratado em load()
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [load, startScanPolling])

  // Limpa timers ao desmontar
  useEffect(
    () => () => {
      if (pollRef.current) clearTimeout(pollRef.current)
      if (refreshPollRef.current) clearTimeout(refreshPollRef.current)
      stopScanPolling()
    },
    [stopScanPolling],
  )

  const accounts = useMemo(() => pivotByAccount(rows), [rows])

  // Resumo derivado das rows
  const summary = useMemo(() => {
    let ok = 0
    let gap = 0
    let blocked = 0
    for (const acc of accounts) {
      if (accountIsBlocked(acc)) blocked += 1
      if (accountHasGap(acc)) gap += 1
      else ok += 1
    }
    return { total: accounts.length, ok, gap, blocked }
  }, [accounts])

  const visibleAccounts = useMemo(() => {
    const q = search.trim().toLowerCase()
    return accounts.filter((acc) => {
      if (filter === 'gap' && !accountHasGap(acc)) return false
      if (q) {
        const hay = `${acc.account_id} ${acc.tenant_id ?? ''} ${acc.tenant_name ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [accounts, filter, search])

  const handleRefreshAll = useCallback(async () => {
    // Captura o baseline antes de disparar o refresh.
    const currentBaseline = scanRun?.started_at ?? null
    baselineStartedAtRef.current = currentBaseline

    setRefreshing(true)
    try {
      await refreshCoverage()
      // Entra no estado scanning + inicia polling.
      setScanning(true)
      startScanPolling(currentBaseline)
    } catch (err) {
      setRefreshing(false)
      notifications.show({
        color: 'red',
        title: 'Falha ao atualizar',
        message: formatApiError(err, 'Não foi possível iniciar a varredura'),
      })
    }
  }, [scanRun, startScanPolling])

  const openCell = useCallback((acc: AccountMatrixRow, role: string) => {
    setSelected({
      account_id: acc.account_id,
      role_name: role,
      row: acc.byRole[role],
    })
  }, [])

  const closeDrawer = useCallback(() => {
    setSelected(null)
  }, [])

  // Determina se a célula selecionada pode ser provisionada.
  const canProvisionSelected = useMemo(() => {
    if (!selected || !selected.row) return false
    const { role_name } = selected.row
    if (!PROVISIONABLE_ROLES.has(role_name)) return false
    const status = rowStatus(selected.row)
    return status === 'missing' || status === 'trust_broken'
  }, [selected])

  const pollJob = useCallback(
    (jobId: number) => {
      pollStartedAtRef.current = Date.now()

      const tick = async () => {
        // Timeout de segurança: job agora aguarda até 5 min pelo StackSet.
        if (
          pollStartedAtRef.current !== null &&
          Date.now() - pollStartedAtRef.current >= PROVISION_JOB_POLL_TIMEOUT_MS
        ) {
          setProvisioning(false)
          notifications.show({
            color: 'yellow',
            title: 'Provisionamento demorado',
            message:
              'A operação está demorando mais que o esperado — verifique o status em instantes.',
          })
          return
        }

        try {
          const job = await getProvisionJob(jobId)
          setProvJob(job)
          if (TERMINAL_JOB_STATUS.has(job.status)) {
            setProvisioning(false)
            if (job.status === 'failed') {
              notifications.show({
                color: 'red',
                title: 'Provisionamento falhou',
                message: job.error || 'A criação da role falhou.',
              })
            } else {
              notifications.show({
                color: job.status === 'noop' ? 'gray' : 'green',
                title:
                  job.status === 'noop'
                    ? 'Nada a fazer'
                    : 'Role provisionada',
                message:
                  job.status === 'noop'
                    ? 'A role já existia / nada a alterar.'
                    : `${job.role_name} criada em ${job.account_id}.`,
              })
            }
            // Atualiza a matriz com o novo estado (célula reflete ok, sem reload).
            await load().catch(() => {})
            return
          }
          pollRef.current = setTimeout(tick, PROVISION_JOB_POLL_INTERVAL_MS)
        } catch (err) {
          setProvisioning(false)
          notifications.show({
            color: 'red',
            title: 'Erro no acompanhamento',
            message: formatApiError(err, 'Falha ao consultar o job'),
          })
        }
      }
      pollRef.current = setTimeout(tick, PROVISION_JOB_POLL_INTERVAL_MS)
    },
    [load],
  )

  const handleConfirmProvision = useCallback(async () => {
    if (!selected) return
    const { account_id, role_name } = selected
    setConfirmOpen(false)
    setProvisioning(true)
    setProvJob(null)
    try {
      const { job_id } = await provisionRole(account_id, role_name)
      setProvJob({
        id: job_id,
        account_id,
        role_name,
        status: 'pending',
        operation_id: null,
        error: null,
        requested_by: null,
        created_at: new Date().toISOString(),
        started_at: null,
        finished_at: null,
      })
      pollJob(job_id)
    } catch (err) {
      setProvisioning(false)
      notifications.show({
        color: 'red',
        title: 'Falha ao criar role',
        message: formatApiError(err, 'Não foi possível enfileirar o job'),
      })
    }
  }, [selected, pollJob])

  if (loading) {
    return (
      <Stack gap="md" p="xl">
        <Loader size="sm" />
      </Stack>
    )
  }

  return (
    <Stack gap="md" p="xl">
      {/* Acao no topo do conteudo: o layout desenha titulo, trilha e descricao, mas nao tem
          como conhecer a acao de cada pagina. */}
      <Group justify="flex-end">
        <Button
          onClick={handleRefreshAll}
          loading={scanning || refreshing}
          disabled={scanning}
          data-testid="refresh-all"
        >
          {scanning ? 'Varrendo…' : 'Atualizar tudo'}
        </Button>
      </Group>

      {/* Card de saúde da detecção FinOps */}
      <FinopsDetectionHealthCard />

      {loadError && (
        <Alert color="red" title="Erro ao carregar">
          {loadError}
        </Alert>
      )}

      {/* Resumo */}
      <Group gap="md">
        <Card withBorder padding="sm" data-testid="summary-ok">
          <Text size="xs" c="dimmed">
            Contas OK
          </Text>
          <Text fw={700} size="lg" c={scanning ? 'dimmed' : 'green'}>
            {scanning ? '—' : summary.ok}
          </Text>
        </Card>
        <Card withBorder padding="sm" data-testid="summary-gap">
          <Text size="xs" c="dimmed">
            Com gap
          </Text>
          <Text fw={700} size="lg" c={scanning ? 'dimmed' : 'red'}>
            {scanning ? '—' : summary.gap}
          </Text>
        </Card>
        <Card withBorder padding="sm" data-testid="summary-blocked">
          <Text size="xs" c="dimmed">
            Bloqueadas (denied)
          </Text>
          <Text fw={700} size="lg" c="dimmed">
            {scanning ? '—' : summary.blocked}
          </Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">
            Última varredura
          </Text>
          <Text size="sm">{fmtDateTime(scanRun?.finished_at ?? null)}</Text>
        </Card>
      </Group>

      {/* Controles */}
      <Group justify="space-between">
        <SegmentedControl
          value={filter}
          onChange={(v) => setFilter(v as 'gap' | 'all')}
          data-testid="filter-control"
          data={[
            { label: 'Só com gap', value: 'gap' },
            { label: 'Todas', value: 'all' },
          ]}
        />
        <TextInput
          placeholder="Buscar por conta ou tenant"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          data-testid="search-input"
          w={280}
        />
      </Group>

      {/* Estado de varredura em andamento — substitui a tabela */}
      {scanning ? (
        <Center
          py="xl"
          style={{ minHeight: 200 }}
          data-testid="scanning-state"
        >
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed" ta="center">
              Varredura em andamento… isso leva ~1–2 min
            </Text>
            <Text size="xs" c="dimmed">
              Atualizando automaticamente quando concluir.
            </Text>
          </Stack>
        </Center>
      ) : (
        /* Matriz */
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Conta (tenant)</Table.Th>
              {ROLE_COLUMNS.map((role) => (
                <Table.Th key={role}>
                  <Tooltip label={role} withArrow>
                    <span>{ROLE_LABELS[role] ?? role}</span>
                  </Tooltip>
                </Table.Th>
              ))}
              <Table.Th>
                <Tooltip
                  label="O que o tenant consegue FAZER, medido ação por ação pelo validador diário. As colunas de papel dizem se a plataforma consegue assumir a role; esta diz se os módulos funcionam."
                  multiline
                  maw={340}
                  withArrow
                >
                  <span>Capacidades</span>
                </Tooltip>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleAccounts.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={ROLE_COLUMNS.length + 2}>
                  <Text c="dimmed" ta="center" py="md">
                    Nenhuma conta para os filtros atuais.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              visibleAccounts.map((acc) => (
                <Table.Tr
                  key={acc.account_id}
                  data-testid={`account-row-${acc.account_id}`}
                >
                  <Table.Td>
                    {acc.tenant_name ? (
                      <>
                        <Group gap={6} wrap="nowrap" align="center">
                          <Text size="sm" fw={600} data-testid={`tenant-name-${acc.account_id}`}>
                            {acc.tenant_name}
                          </Text>
                          {acc.tenant_environment && (
                            <Badge size="xs" variant="filled" tt="uppercase">
                              {acc.tenant_environment.toUpperCase()}
                            </Badge>
                          )}
                          {acc.co_status && (
                            <CoStatusChip status={acc.co_status} />
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {acc.account_id}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Group gap={6} wrap="nowrap" align="center">
                          <Text size="sm" ff="monospace" data-testid={`tenant-name-${acc.account_id}`}>
                            {acc.account_id}
                          </Text>
                          {acc.co_status && (
                            <CoStatusChip status={acc.co_status} />
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {acc.tenant_id == null ? 'payer' : `tenant: ${acc.tenant_id}`}
                        </Text>
                      </>
                    )}
                  </Table.Td>
                  {ROLE_COLUMNS.map((role) => {
                    const cell = acc.byRole[role]
                    const status = cell?.status ?? 'not_applicable'
                    const color = statusColor(status)
                    const isNA = status === 'not_applicable'
                    const showActionsIndicator =
                      status === 'ok' && FINOPS_ROLES.has(role) && cell != null
                    return (
                      <Table.Td key={role}>
                        <Group gap={4} wrap="nowrap" align="center">
                          <Badge
                            variant={isNA ? 'transparent' : 'light'}
                            color={color === 'dimmed' ? 'gray' : color}
                            c={isNA ? 'dimmed' : undefined}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openCell(acc, role)}
                            data-testid={`cell-${acc.account_id}-${role}`}
                          >
                            {isNA ? '–' : status}
                          </Badge>
                          {showActionsIndicator && cell && (
                            <ActionsIndicator row={cell} />
                          )}
                        </Group>
                      </Table.Td>
                    )
                  })}
                  <Table.Td data-testid={`capacidades-${acc.account_id}`}>
                    <Capacidades
                      falha={capacidades[acc.account_id]?.features_falha}
                      naoMedidas={capacidades[acc.account_id]?.features_nao_medidas}
                    />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      )}

      {/* Drawer de detalhe da célula */}
      <Drawer
        opened={selected != null}
        onClose={closeDrawer}
        title="Detalhe da role"
        position="right"
        size="md"
      >
        {selected && (
          <div data-testid="cell-drawer">
            <Stack gap="sm">
              <div>
                <Text size="xs" c="dimmed">
                  Conta
                </Text>
                <Text ff="monospace">{selected.account_id}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Role
                </Text>
                <Text>{selected.role_name}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Status
                </Text>
                <Badge
                  color={
                    statusColor(rowStatus(selected.row)) === 'dimmed'
                      ? 'gray'
                      : statusColor(rowStatus(selected.row))
                  }
                >
                  {rowStatus(selected.row)}
                </Badge>
              </div>
              <Divider />
              <div>
                <Text size="xs" c="dimmed">
                  Método de detecção
                </Text>
                <Text size="sm">{selected.row?.detection_method ?? '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Código
                </Text>
                <Text size="sm">{selected.row?.detail_code ?? '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Mensagem
                </Text>
                {selected.row?.detail_message ? (
                  <Code block>{selected.row.detail_message}</Code>
                ) : (
                  <Text size="sm">—</Text>
                )}
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Última verificação
                </Text>
                <Text size="sm">
                  {fmtDateTime(selected.row?.last_checked_at ?? null)}
                </Text>
              </div>

              <Divider />

              {/* Explicações para status sem ação direta */}
              {rowStatus(selected.row) === 'denied' && (
                <Alert color="gray" title="Acesso negado">
                  Não foi possível entrar na conta — falta a
                  OrganizationAccountAccessRole. Resolva manualmente na AWS antes
                  de provisionar as roles do ConsoleAI.
                </Alert>
              )}
              {rowStatus(selected.row) === 'not_applicable' && (
                <Alert color="gray" title="Não aplicável">
                  Conta payer (gestão) — as roles do ConsoleAI não se aplicam.
                </Alert>
              )}

              {/* Ação de remediação: provisioning (missing/trust_broken) */}
              {canProvisionSelected && (
                <Button
                  color="blue"
                  loading={provisioning}
                  onClick={() => setConfirmOpen(true)}
                  data-testid="cell-provision-btn"
                >
                  Criar role
                </Button>
              )}

              {/* Remediação de actions FinOps: actions_missing numa role com status ok */}
              {selected?.row &&
                rowStatus(selected.row) === 'ok' &&
                selected.row.actions_status === 'actions_missing' && (
                  <>
                    {selected.row.template_grants_missing === true ? (
                      <Alert
                        color="orange"
                        title="Template precisa ser atualizado"
                        data-testid="actions-template-missing-alert"
                      >
                        <Text size="sm">
                          Template precisa incluir:{' '}
                          <Code>
                            {(selected.row.missing_actions ?? []).join(', ') || '(ações não listadas)'}
                          </Code>
                        </Text>
                        <Text size="xs" c="dimmed" mt="xs">
                          Adicione as actions ao template StackSet e re-provisione a role.
                        </Text>
                      </Alert>
                    ) : (
                      <Button
                        color="teal"
                        variant="light"
                        loading={provisioning}
                        onClick={() => setConfirmOpen(true)}
                        data-testid="cell-remediar-btn"
                      >
                        Remediar actions
                      </Button>
                    )}
                  </>
                )}

              {/* Progresso do provisionamento */}
              {provJob && provJob.account_id === selected.account_id &&
                provJob.role_name === selected.role_name && (
                  <Alert
                    color={
                      provJob.status === 'failed'
                        ? 'red'
                        : provJob.status === 'success'
                          ? 'green'
                          : 'blue'
                    }
                    title="Provisionamento"
                    data-testid="prov-progress"
                  >
                    <Group gap="xs">
                      {!TERMINAL_JOB_STATUS.has(provJob.status) && (
                        <Loader size="xs" />
                      )}
                      <Text size="sm">Status: {provJob.status}</Text>
                    </Group>
                    {provJob.error && (
                      <Code block mt="xs">
                        {provJob.error}
                      </Code>
                    )}
                  </Alert>
                )}
            </Stack>
          </div>
        )}
      </Drawer>

      {/* Modal de confirmação de provisionamento */}
      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmar criação de role"
      >
        {selected && (
          <div data-testid="provision-modal">
            <Stack gap="sm">
              <Text>
                Criar a role <b>{selected.role_name}</b> na conta{' '}
                <Code>{selected.account_id}</Code>?
              </Text>
              <Text size="sm" c="dimmed">
                A role será criada via StackSet. A operação roda em background e
                pode levar alguns instantes.
              </Text>
              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setConfirmOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  color="blue"
                  onClick={handleConfirmProvision}
                  data-testid="provision-confirm"
                >
                  Criar role
                </Button>
              </Group>
            </Stack>
          </div>
        )}
      </Modal>
    </Stack>
  )
}

export default AwsRoleCoverage
